import React, { FC, HTMLAttributes, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { Autocomplete, Box, Button, TextField, Typography } from '@mui/material';
import { Organization } from 'models/organization';
import { useQuery } from '@apollo/client';
import { useDenthubErrorBoundary } from 'components/shared/denthub-error-boundary/denthub-error-boundary-context';
import { AddTransportContext } from 'pages/transport/add-transport/add-transport-context';
import { transportClinicsTemplate } from 'apollo/templates/transport/query.graphql';

type TransportClinicStepProps = {
    onNavigateNext: () => void;
    onNavigateBack?: () => void;
};
const TransportClinicStep: FC<TransportClinicStepProps> = ({
    onNavigateNext,
    onNavigateBack,
}: TransportClinicStepProps) => {
    const { t: translate } = useTranslation(['common', 'transport']);
    const { transportOrderReq, setTransportOrderReq } = useContext(AddTransportContext);
    const { showBoundary } = useDenthubErrorBoundary();

    const {
        data: { transportClinics } = {},
        loading: clinicsLoading,
        error: clinicsError,
    } = useQuery(transportClinicsTemplate, {
        variables: { labId: transportOrderReq.lab ? transportOrderReq.lab._id : undefined },
        onError(error) {
            showBoundary(error);
        },
    });

    const clinicsRenderOptions = (props: HTMLAttributes<HTMLLIElement>, clinic: Organization) => (
        <li {...{ ...props, key: clinic?._id }}>
            <Box>
                <Box>{clinic.name}</Box>
                <Box>
                    <Typography variant="caption">
                        {clinic.contact.address.street}, {clinic.contact.address.city}
                    </Typography>
                </Box>
            </Box>
        </li>
    );

    return (
        <Box>
            <Box mb={4}>
                <Autocomplete
                    loading={clinicsLoading}
                    options={transportClinics || []}
                    onChange={(_, clinic) =>
                        setTransportOrderReq({ ...{ ...transportOrderReq, clinic: clinic, date: null } })
                    }
                    value={transportOrderReq.clinic}
                    renderOption={clinicsRenderOptions}
                    getOptionLabel={(option: Organization) => option?.name || ''}
                    isOptionEqualToValue={(option: Organization, value: Organization) => option?._id === value?._id}
                    renderInput={(params) => (
                        <TextField {...params} label={translate('common:CLINIC')} fullWidth={true} />
                    )}
                />
            </Box>
            <Box display="flex" justifyContent="flex-end">
                {onNavigateBack ? (
                    <Box flexGrow={1}>
                        <Button onClick={onNavigateBack}>{translate('common:BACK')}</Button>
                    </Box>
                ) : undefined}

                <Button variant="contained" onClick={onNavigateNext} disabled={!transportOrderReq.clinic}>
                    {translate('common:NEXT_STEP')}
                </Button>
            </Box>
        </Box>
    );
};

export default TransportClinicStep;
